import { ReactElement } from 'react';
import { Button, Divider, Typography } from '@mui/material';

import { useConnection } from 'modules/auth/hooks/useConnection';
import { BorderedPaper } from 'modules/common/components/BorderedPaper';
import { ShareIcon, stakeTokenIcon } from 'modules/common/icons';
import { useGlobalTranslation } from 'modules/i18n/hooks/useGlobalTranslation';
import { useGetLastRewardDistributionDateQuery } from 'modules/liquidStaking/actions/getLastRewardDistributionDate';
import { DeprecatedContent } from 'modules/liquidStaking/components/DeprecatedContent';
import { Explore } from 'modules/liquidStaking/components/Explore';
import { RatioLastUpdate } from 'modules/liquidStaking/components/RatioUpdate';
import { TokenDetails } from 'modules/liquidStaking/components/TokenDetails/TokenDetails';
import { useLiquidStakingPool } from 'modules/liquidStaking/hooks/useLiquidStakingPool';
import { useStakeToken } from 'modules/liquidStaking/hooks/useStakeToken';

import { translation } from './translation';
import { useStyles } from './useStyles';

const EXPLORE_LINK = 'https://www.matchain.io/';

export function Details(): ReactElement {
  const { t, keys } = useGlobalTranslation(translation);
  const { classes } = useStyles();
  const { cacheTime } = useConnection();

  const { poolAddress, isDeprecated } = useLiquidStakingPool();
  const { name, address, explorerLink } = useStakeToken(poolAddress);
  const { data: lastRewardDate } = useGetLastRewardDistributionDateQuery(
    undefined,
    { refetchOnMountOrArgChange: cacheTime },
  );

  return (
    <BorderedPaper className={classes.root}>
      {isDeprecated ? (
        <DeprecatedContent />
      ) : (
        <div className={classes.content}>
          <div>
            <div className={classes.top}>
              <TokenDetails
                address={address}
                icon={stakeTokenIcon}
                title={t(keys.tokens.stakeToken)}
              />

              <Button
                className={classes.shareButton}
                color="secondary"
                component="a"
                href={explorerLink}
                rel="noreferrer"
                target="_blank"
                variant="outlined"
              >
                <span>{t(keys.common.open)}</span>

                <ShareIcon />
              </Button>
            </div>

            <Typography
              color={theme => theme.palette.text.secondary}
              mt={6}
              variant="body1"
            >
              {t(keys.description, {
                stakeToken: name,
                mainToken: t(keys.tokens.main),
              })}
            </Typography>
          </div>

          {EXPLORE_LINK && (
            <>
              <Divider />

              <Explore link={EXPLORE_LINK} />
            </>
          )}

          {lastRewardDate && (
            <>
              <Divider />

              <RatioLastUpdate startDate={lastRewardDate} />
            </>
          )}
        </div>
      )}
    </BorderedPaper>
  );
}
